import { PencilIcon } from '@heroicons/react/outline';
import { InformationCircleIcon } from '@heroicons/react/solid';
import { Grid, IconButton, Typography } from '@mui/material';
import { FastField, useField } from 'formik';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';
import { OpenInNew } from '@mui/icons-material';
import moment from 'moment';

import { fedexRedirect, getDateOnly, queryParams } from '@shared/commonFunctions';
import { IFormFieldConfig } from '@shared/components/FormFieldsConstructor';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { downPaymentStatuses } from '@shared/constants/lists';
import { STATECODES } from '@shared/constants/StateCodes';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { TextInputField } from '@shared/fields/TextInputField';
import { SaveButton } from '@shared/inputs/Buttons';
import { selectReCalculateDeal, setDealReCalculate, setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { CommonButtonWrapper } from '@styles/CommonStyles';
import { ConditionsBoxShadow } from '@styles/ContactManager/DealStyles';
import { muiTheme } from '@styles/muiTheme';
import { ConditionsSection, FieldGroup, FormControl } from '@styles/StyledDiv';

import { DealsButton } from '@styles/StyledButtons';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Roles } from '@shared/constants/constants';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { StyledCheckboxGeneral } from '@styles/StyledCheckbox';
import { IConditionsProp, IDeal, IDealCalculate } from '../interfaces';
import { requestConditionCalculate } from '../queries';
import { DealFeesModal } from './DealFees/DealFeesModal';
import { DownPaymentModal } from './DownPaymentModal';
import { SalesTaxInfoModal } from './SalesTaxInfoModal';
import { LENDER_CODES } from './constants';

const DOCS_OUT_TRKR_ID = 4;

const grossUpModalAlertContent = {
  check: {
    subtitle: ' will be multiplied by 1.25% and deal will be updated. Do you want to continue?',
  },
  uncheck: {
    subtitle:
      ' will be divided by 1.25%, returning to its original value, and deal will be updated. Do you want to continue?',
  },
};

const applicableIncomeFieldsLabels = {
  SocialSecuritySsi: 'Social Security Ssi',
  Disability: 'Disability',
} as const;


export const ConditionsForm = ({
  deal = {} as IDeal,
  otherIncomes,
  setFieldValue,
  values,
  initialValues,
  isLoading,
  reloadDeal,
  reloadDeals,
  dealSavePreValidation,
}: IConditionsProp) => {
  const dispatch = useDispatch();
  const { financeConditionItem } = deal;
  const [isFeesOpen, setFeesOpen] = useState(0);
  const [isCashDownBold, setIsCashDownBold] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTaxRateCalculation, setIsTaxRateCalculation] = useState(true);
  const [loading, setLoading] = useState(false);
  const [, grossUpMeta, grossUpHelpers] = useField('isGrossUpIncome');

  const [pendingGrossUpAction, setPendingGrossUpAction] = useState<'check' | 'uncheck' | null>(null);

  const [isDownPaymentModalOpen, setDownPaymentModalOpen] = useState<boolean>(false);
  const [feesType, setFeesType] = useState<string>('');

  const contractDateRef = useRef<string>(values?.financeConditionItem?.contractDate || '');
  const firstPaymentDateRef = useRef<string>(values?.financeConditionItem?.firstPaymentDate || '');

  const contactData = useSWR(`/${SERVICES.CONTACT}/contacts/${queryParams()?.id}/header`);
  const reCalculateDeal = useSelector(selectReCalculateDeal);
  
 const { userHasRoles } = useUserAccount();
  const useAprBasedCalculation = deal?.lenderResponse?.useAprBasedCalculation;

  const request: IDealCalculate = useMemo(() => {
    return {
      salesPrice: deal?.financeConditionItem?.salesPrice,
      cashBackAmount: deal?.financeConditionItem?.cashBackAmount,
      cashDownAmount: deal?.financeConditionItem?.cashDownAmount,
      dealId: deal?.id,
      dealFees: values?.dealFeeArray?.concat(values?.additionalFeeArray || []),
      lenderCode: deal?.lenderResponse?.lenderCode,
      contractDate: deal?.financeConditionItem?.contractDate,
      firstPaymentDate: deal?.financeConditionItem?.firstPaymentDate,
      daysToFirstPayment: deal?.financeConditionItem?.daysToFirstPayment,
      term: deal?.financeConditionItem?.term,
      rate: deal?.financeConditionItem?.rate,
      state: STATECODES[contactData?.data?.state as keyof typeof STATECODES],
      totalSalesTaxPercentage: deal?.financeConditionItem?.totalSalesTaxPercentage,
      isTaxRateCalculation: false,
      totalSalesTaxAmount: deal?.financeConditionItem?.totalSalesTaxAmount,
      totalProductTaxPercentage: deal?.financeConditionItem?.totalProductTaxPercentage,
      isRecalculatingTitleAndDocFees: false,
    };
  }, [deal, values]);

  // reCalculate trigger from other components like Rate and terms are updated from CUDL document once uploaded
  useEffect(() => {
    const refreshData = async () => {
      if (reCalculateDeal) {
        await calculate(request);
        dispatch(setDealReCalculate(false));
      }
    };

    refreshData();
  }, [reCalculateDeal, request]);

  useEffect(() => {
    const shouldCalculate =
      deal?.financeConditionItem &&
      values?.financeConditionItem &&
      (deal.financeConditionItem?.apr !== values.financeConditionItem?.apr ||
        deal.financeConditionItem?.interest !== values?.financeConditionItem?.interest);

    if (shouldCalculate) {
      dispatch(setDealReCalculate(true));
    }
  }, [values?.financeConditionItem?.apr, values?.financeConditionItem?.interest]);

  const hideUpdateBtn = useMemo(
    () => !!deal?.currentSteps?.find((step) => step.trackerStageId >= DOCS_OUT_TRKR_ID),
    [deal?.currentSteps]
  );


const includedOtherIncomesTypes = useMemo(() => {
  if (!Array.isArray(otherIncomes)) return [];

  const labels = otherIncomes
    .filter(
      ({ incomeType, isCurrent }) =>
        incomeType in applicableIncomeFieldsLabels && isCurrent
    )
    .map(
      ({ incomeType }) =>
        applicableIncomeFieldsLabels[
          incomeType as keyof typeof applicableIncomeFieldsLabels
        ]
    );
  return Array.from(new Set(labels));
}, [otherIncomes]);


const isEnableGrossUp = useMemo(() => {
  if (
    userHasRoles([Roles.BANK, Roles.BANK_MANAGER, Roles.LC_MANAGER]) &&
    deal.lenderResponse?.lenderCode.toUpperCase() === LENDER_CODES.HUGHES &&
    includedOtherIncomesTypes?.length > 0
  ) {
    return true;
  }
  return false;
}, [userHasRoles, deal.lenderResponse?.lenderCode, includedOtherIncomesTypes]);

  const calculate = useCallback(
    async (request: IDealCalculate, isRecalculatingTitleAndDocFees = false) => {
      setLoading(true);
      // deal calculate save the deal also.
      if (dealSavePreValidation()) {
        const payload = {
          ...request,
        };
        payload.isTaxRateCalculation = isTaxRateCalculation;
        payload.contractDate = getDateOnly(payload.contractDate?.toString() ?? '');
        payload.firstPaymentDate = getDateOnly(payload.firstPaymentDate?.toString() ?? '');
        payload.isRecalculatingTitleAndDocFees = isRecalculatingTitleAndDocFees;

        await requestConditionCalculate(payload);
        await reloadDeals();
        await reloadDeal();
        dispatch(setLastCommentsRefresh(new Date().toISOString()));
      }
      setLoading(false);
    },
    [request, deal, isTaxRateCalculation]
  );

  const handleChange = useCallback(
    async (data: { target: { name: string; value: number; source: string } }) => {
      const { name, value } = data.target;

      if (name === 'financeConditionItem.cashDownAmount') {
        setIsCashDownBold(value > 0);
      }

      if (name === 'financeConditionItem.totalSalesTaxPercentage') {
        setIsTaxRateCalculation(true);
      }

      if (name === 'financeConditionItem.totalSalesTaxAmount') {
        setIsTaxRateCalculation(false);
      }

      await setFieldValue?.(name, value);
    },
    [
      financeConditionItem?.salesPrice,
      financeConditionItem?.rateMarkUpPercentage,
      financeConditionItem?.cashDownAmount,
      financeConditionItem?.cashBackAmount,
      financeConditionItem?.totalProductAmount,
      financeConditionItem?.totalSalesTaxPercentage,
      financeConditionItem?.totalSalesTaxAmount,
      financeConditionItem?.lastPaymentDate,
      financeConditionItem?.estimatedGrossTotalAmount,
      financeConditionItem?.subTotal,
      financeConditionItem?.subTotalLessDocFee,
      financeConditionItem?.daysToFirstPayment,
    ]
  );

  const handleBlur = useCallback(() => {
    dispatch(setDealReCalculate(true));
  }, []);

  const handleFeesOpen = useCallback((type: string) => {
    setFeesType(type);
    setFeesOpen((prevState) => prevState + 1);
  }, []);

  const handleFeesClose = useCallback(async () => {
    setFeesType('');
    setFeesOpen(0);
  }, [request]);

  const handleOnEditCashDown = () => {
    if (initialValues?.financeConditionItem?.cashDownAmount !== values?.financeConditionItem.cashDownAmount) {
      Notistack.infoMessage('Save CashDown before proceeding');
    } else {
      setDownPaymentModalOpen(true);
    }
  };

  const handleDateBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>, value: string, dateType: 'contractDate' | 'firstPaymentDate') => {
      const inputValue = moment(event.target.value);
      const initialValue = moment(value);
      const isEqual = inputValue.isSame(initialValue);

      if (!isEqual) {
        handleBlur();
      }
      const dateRefs = {
        contractDate: contractDateRef,
        firstPaymentDate: firstPaymentDateRef,
      };

      dateRefs[dateType].current = event.target.value;
    },
    [handleBlur]
  );

  const handleDateKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      (event.target as HTMLInputElement).blur();
    }
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleBlur();
      (event.target as HTMLInputElement).blur();
    }
  }, []);

  const handleGrossUpChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      setPendingGrossUpAction(checked ? 'check' : 'uncheck');
    },
    [otherIncomes]
  );

  const handleGrossUpConfirm = useCallback(() => {
    grossUpHelpers.setValue(pendingGrossUpAction === 'check');
    setPendingGrossUpAction(null);
  }, [grossUpHelpers, pendingGrossUpAction, handleBlur]);

  const handleGrossUpCancel = useCallback(() => {
    setPendingGrossUpAction(null);
  }, []);

  const handleUpdate = useCallback(async () => {
    calculate(request, true);
  }, [request]);

  const leftPart = useMemo(
    () => [
      {
        name: 'financeConditionItem.contractDate',
        label: 'Contract Date',
        component: SelectDateField,
        onBlur: (event: React.FocusEvent<HTMLInputElement>) => handleDateBlur(event, contractDateRef.current, 'contractDate'),
        onKeyDown: handleDateKeyDown,
      },
      {
        name: 'financeConditionItem.daysToFirstPayment',
        label: 'Days to First Payment',
        component: NumberInputField,
        onChange: handleChange,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown,
        validateOnBlur: true,
      },
      {
        name: 'financeConditionItem.firstPaymentDate',
        label: 'First Payment',
        component: SelectDateField,
        onBlur: (event: React.FocusEvent<HTMLInputElement>) =>
          handleDateBlur(event, firstPaymentDateRef.current, 'firstPaymentDate'),
        onKeyDown: handleDateKeyDown,
      },
      {
        name: 'financeConditionItem.lastPaymentDate',
        label: 'Final Payment',
        component: SelectDateField,
        disabled: true,
        withoutTimeZone: true,
      },
      {
        name: 'financeConditionItem.totalSalesTaxPercentage',
        label: 'Sales Tax',
        suffix: '%',
        decimalScale: 2,
        highlightFullBorder: financeConditionItem?.sorRequired,
        component: NumberInputField,
        onInfo: () => setIsModalOpen(true),
        onChange: handleChange,
        onBlur: handleBlur,
        validateOnBlur: true,
      },
      {
        name: 'financeConditionItem.totalProductTaxPercentage',
        label: 'Product Tax',
        suffix: '%',
        decimalScale: 2,
        component: NumberInputField,
        disabled: true,
      },
      {
        name: 'financeConditionItem.monthlyPaymentAmount',
        label: 'Payment Amt',
        decimalScale: 2,
        prefix: '$',
        component: NumberInputField,
        bold: true,
        disabled: true,
      },
      {
        name: 'financeConditionItem.lastPaymentAmount',
        label: 'Last Payment',
        decimalScale: 2,
        prefix: '$',
        component: NumberInputField,
        bold: true,
        disabled: true,
      },
      {
        name: 'financeConditionItem.estimatedGrossTotalAmount',
        label: 'Estimated Gross',
        decimalScale: 2,
        prefix: '$',
        allowNegative: true,
        component: NumberInputField,
        onChange: handleChange,
      },
      {
        name: 'financeConditionItem.rate',
        label: 'Interest Rate',
        decimalScale: 2,
        suffix: '%',
        component: NumberInputField,
        disabled: true,
        hidden: useAprBasedCalculation,
      },
      {
        name: 'financeConditionItem.apr',
        label: 'APR',
        decimalScale: 2,
        suffix: '%',
        component: NumberInputField,
        disabled: true,
        hidden: !useAprBasedCalculation,
      },
      {
        name: 'financeConditionItem.rateMarkUpPercentage',
        label: 'Rate Markup',
        decimalScale: 2,
        suffix: '%',
        component: NumberInputField,
        onChange: handleChange,
      },
      {
        name: 'carrierTrackingNumberOut',
        label: 'Tracking In',
        component: TextInputField,
        value: deal.carrierTrackingNumberOut,
        onButtonClick: () => fedexRedirect(deal.carrierTrackingNumberOut),
      },
      {
        name: 'carrierTrackingNumberIn',
        label: 'Tracking Out',
        component: TextInputField,
        value: deal.carrierTrackingNumberIn,
        onButtonClick: () => fedexRedirect(deal.carrierTrackingNumberIn),
      },
      {
        name: 'isAch',
        label: 'ACH',
        component: CheckboxField,
      },

      {
        name: 'postLoanDti',
        label: 'Post Loan Dti',
        decimalScale: 2,
        suffix: '%',
        hidden: !(deal.lenderResponse?.isQuilo || deal.lenderResponse?.lenderCode.toUpperCase() === LENDER_CODES.QLO),
        component: NumberInputField,
      }
    ],
    [
      financeConditionItem,
      handleDateBlur,
      handleBlur,
      deal.lenderResponse?.isQuilo,
      deal.lenderResponse?.lenderCode,
      handleDateKeyDown,
      handleKeyDown
    ]
  );

  const rightPart = [
    {
      name: 'financeConditionItem.salesPrice',
      label: 'Sales Price',
      decimalScale: 2,
      bold: true,
      onChange: handleChange,
      onBlur: handleBlur,
      validateOnBlur: true,
    },
    {
      name: 'financeConditionItem.totalProductAmount',
      label: 'Products +',
      disabled: true,
      decimalScale: 2,
    },
    {
      name: 'financeConditionItem.feesTotal',
      label: 'Deal Fees +',
      decimalScale: 2,
      disabled: true,
      onEdit: () => handleFeesOpen('Deal Fees'),
      onUpdate: handleUpdate,
      hideUpdate: hideUpdateBtn,
    },

    {
      name: 'financeConditionItem.subTotal',
      label: 'Sub Total',
      decimalScale: 2,
      disabled: true,
      hidden: useAprBasedCalculation,
    },
    {
      name: 'financeConditionItem.subTotalLessDocFee',
      label: 'Sub Total Less Doc Fee',
      decimalScale: 2,
      disabled: true,
      hidden: !useAprBasedCalculation,
    },
    {
      name: 'financeConditionItem.totalSalesTaxAmount',
      label: 'Taxes +',
      decimalScale: 2,
      highlightFullBorder: financeConditionItem?.sorRequired,
      onChange: handleChange,
      onBlur: handleBlur,
      validateOnBlur: true,
    },
    {
      name: 'financeConditionItem.totalProductTaxAmount',
      label: 'Product Taxes +',
      decimalScale: 2,
      disabled: true,
    },
    {
      name: 'financeConditionItem.cashBackAmount',
      label: 'Cash Back +',
      decimalScale: 2,
      onChange: handleChange,
      onBlur: handleBlur,
      validateOnBlur: true,
    },
    {
      name: 'financeConditionItem.cashDownAmount',
      label: 'Cash Down -',
      decimalScale: 2,
      onChange: handleChange,
      onBlur: handleBlur,
      validateOnBlur: true,
      onEdit: handleOnEditCashDown,
      disabled: false,
      bold: isCashDownBold,
    },
    {
      name: 'financeConditionItem.additionalFeesTotal',
      label: 'Additional Fees',
      decimalScale: 2,
      disabled: true,
      onEdit: () => handleFeesOpen('Additional Fees'),
    },
    {
      name: 'financeConditionItem.totalAmountFinanced',
      label: 'Total Financed',
      decimalScale: 2,
      bold: true,
      disabled: true,
    },
    {
      name: 'financeConditionItem.interest',
      label: 'Interest',
      decimalScale: 2,
      disabled: true,
      hidden: useAprBasedCalculation,
    },
    {
      name: 'financeConditionItem.interestPlusDocFee',
      label: 'Interest Plus Doc Fee +',
      decimalScale: 2,
      disabled: true,
      hidden: !useAprBasedCalculation,
    },
    {
      name: 'financeConditionItem.dealTotal',
      label: 'Total + Interest',
      decimalScale: 2,
      disabled: true,
    },
  ].map(
    (i) => ({
      ...i,
      prefix: '$',
      component: NumberInputField,
    }),
    [financeConditionItem?.sorRequired, hideUpdateBtn]
  );

  const rightBottomPart = [
    {
      name: 'downPaymentStatus',
      label: 'Down Pmt.',
      bold: true,
      options: downPaymentStatuses,
      removeExtraPadding: true,
      component: SelectAutocompleteField,
    },
    {
      name: 'cudlAccountNumber',
      label: 'App #',
      bold: true,
      highlightFullBorder: true,
      component: TextInputField,
    },
  ];

  const renderFields = <T extends object>(fieldsList: IFormFieldConfig<T>[], _value: IDeal) => {
    return fieldsList
      ?.filter((r) => !r.hidden)
      .map(({ name, label, component: Component, onEdit, onButtonClick, onInfo, bold, disabled, onUpdate, hideUpdate = false, updateBtnLabel= 'Update', ...rest }, _idx) => (
        <FormControl key={name}>
          <Typography sx={{ fontWeight: bold ? 700 : 400 }}>{label}</Typography>
          {!hideUpdate && onUpdate ? (
            <DealsButton
              color="primary"
              variant="contained"
              size="small"
              onClick={onUpdate}
            >
              {updateBtnLabel}
            </DealsButton>
          ) : null}

          <FieldGroup>
            <FastField
              {...rest}
              name={name}
              component={Component}
              disabled={disabled !== undefined ? disabled : !!onEdit}
            />

            {onEdit ? (
              <IconButton onClick={onEdit as () => void}>
                <PencilIcon width="20px" />
              </IconButton>
            ) : null}

            {onInfo ? (
              <IconButton onClick={onInfo as () => void}>
                <InformationCircleIcon width="20px" />
              </IconButton>
            ) : null}

            {onButtonClick ? (
              <IconButton
                color="primary"
                onClick={onButtonClick as () => void}
              >
                <OpenInNew />
              </IconButton>
            ) : null}
          </FieldGroup>
        </FormControl>
      ));
  };
  return (
    <Grid container>
      <Grid
        item
        xs={6}
      >
        <ConditionsSection>
          {renderFields(leftPart, deal)}

          <FormControl key="isGrossUpIncome">
            <Typography sx={{ fontWeight: 400 }}>Gross Up Income</Typography>
            <FieldGroup>
              <StyledCheckboxGeneral
                id="isGrossUpIncome"
                name="isGrossUpIncome"
                checked={!!grossUpMeta.value}
                disabled={!isEnableGrossUp}
                sx={{ ml: '-11px' }}
                onChange={handleGrossUpChange}
              />
            </FieldGroup>
          </FormControl>
        </ConditionsSection>
      </Grid>
      <Grid
        item
        xs={6}
      >
        <ConditionsBoxShadow>
          <ConditionsSection bg={muiTheme.palette.grey[400]}>{renderFields(rightPart, deal)}</ConditionsSection>
          <ConditionsSection>
            {renderFields(rightBottomPart, deal)}

            <CommonButtonWrapper gap={1}>
              <SaveButton width="65px" />
            </CommonButtonWrapper>
          </ConditionsSection>
        </ConditionsBoxShadow>
      </Grid>

      <LoaderBackdrop open={loading} />

      {!!isFeesOpen && (
        <DealFeesModal
          dealId={deal?.id}
          dealFeesModalPosition={isFeesOpen}
          handleClose={handleFeesClose}
          reloadDeal={reloadDeal}
          type={feesType}
          setFieldValue={setFieldValue}
          dealFeesArr={deal?.dealFeeArray}
          additionalFeeArr={deal?.additionalFeeArray}
          isLoading={isLoading}
          dealSavePreValidation={dealSavePreValidation}
        />
      )}

      {isDownPaymentModalOpen && (
        <DownPaymentModal
          initialDownPayment={deal?.financeConditionItem?.cashDownAmount ?? 0}
          handleClose={() => setDownPaymentModalOpen(false)}
        />
      )}

      {pendingGrossUpAction !== null && (
        <AlertModal
          open={pendingGrossUpAction !== null}
          title="Alert"
          maxWidth="60%"
          buttonText="Ok"
          subtitle={
            pendingGrossUpAction
              ? `The ${includedOtherIncomesTypes.join(' & ')} income field${includedOtherIncomesTypes.length > 1 ? 's' : ''} ${
                  grossUpModalAlertContent[pendingGrossUpAction].subtitle
                }`
              : ''
          }
          onClose={handleGrossUpCancel}
          onConfirm={handleGrossUpConfirm}
        />
      )}

      <SalesTaxInfoModal
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
      />
    </Grid>
  );
};
